// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FormFieldClickable } from "@insite/mobius/FormField";
import { IconWrapper } from "@insite/mobius/Icon";
import { getProp } from "@insite/mobius/utilities";
import { searchInputStyles } from "@steiner/Overrides/Widgets/Header/SearchInput";
import Search from "@steiner/XDL/Icons/Search";
import { color, styledScrollbar, xScreen } from "@steiner/XDL/Utilities";
import { css } from "styled-components";

searchInputStyles.input = {
    iconProps: {
        src: Search,
        color: "common.ui7",
    },
    cssOverrides: {
        formInputWrapper: css`
            & > ${FormFieldClickable} {
                padding: 0;
                width: 35px;
                height: 35px;
                & > ${IconWrapper} {
                    border-radius: 0;
                    width: 100%;
                    height: 100%;
                    padding: 0;
                    display: flex;
                    justify-content: center;
                }

                &.searchClickable {
                    & > ${IconWrapper} {
                        background: ${color("secondary")};
                        transition: all ease-in-out ${getProp("theme.transition.duration.regular")}ms;
                        svg {
                            width: 20px;
                            height: auto;
                        }
                    }

                    &:hover > ${IconWrapper} {
                        background: ${color("common.ui12")};
                    }
                }
            }
        `,
        inputSelect: css`
            height: 39px;
        `,
    },
};

searchInputStyles.autocompleteWrapper = {
    css: css`
        display: flex;
        flex-direction: row;
        max-width: 840px;

        & > div:only-child {
            max-width: 703px;
        }

        .has-product-column {
            &::before {
                content: "";
                background: ${color("common.ui5")};
                width: 1px;
                height: 100%;
                position: absolute;
                left: 0;
                top: 0;
            }
        }

        ${xScreen("lte", "md")} {
            max-width: 100%;
            width: calc(100vw - 32px);
            flex-direction: column;
            max-height: calc(100vh - 140px);
            overflow: auto;

            ${styledScrollbar(4)}

            & > div:only-child {
                max-width: 100%;
            }

            .has-product-column {
                &::before {
                    display: none;
                }
            }
        }

        ${xScreen("lte", "xs")} {
            max-height: 80vh;
        }
    `,
};

searchInputStyles.autocompleteColumnWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        padding: 12px;
        gap: 24px;
        width: 280px;
        height: 100%;

        min-height: 340px;
        max-height: calc(100vh - 62px);

        overflow: auto;
        ${styledScrollbar(4)}

        ${xScreen("lte", "md")} {
            min-height: unset;
            max-height: unset;
            width: 100%;
        }
    `,
};

searchInputStyles.autocompleteColumnProductWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        padding: 12px;
        height: 100%;
        width: 100%;

        min-height: 340px;
        max-height: calc(100vh - 62px);

        overflow: auto;
        ${styledScrollbar(4)}

        ${xScreen("lte", "md")} {
            min-height: unset;
            max-height: unset;
        }
    `,
};

searchInputStyles.floatingTooltip = {
    tooltipContent: {
        css: css`
            border: 1px solid ${color("common.ui5")};
            background-color: ${color("common.white")};
            border-radius: 0;
            padding: 0;
            max-width: 100vw;
            & > :empty {
                display: none;
            }
            ${xScreen("lte", "md")} {
                max-width: unset;
            }
        `,
    },
};

searchInputStyles.inputWrapper = {
    css: css`
        line-height: 0; // to fix extra spacing on firefox
    `,
};

searchInputStyles.searchPopUpWrapper = {
    css: css`
        ${({ width }: any) =>
            css`
                width: ${width}px;
            `}

        ${xScreen("lte", "md")} {
            width: calc(100vw - 32px);
        }
    `,
};
